'use client';
/* eslint-disable max-lines-per-function, complexity */

/**
 * Family / Guardian Access — active family context provider (Unit U4).
 *
 * REQUIREMENTS SUMMARY (U4 — Active family context + Switch Family):
 * - R11/R12/R13 Holds the parent/account-holder's "which household am I acting
 *   as" state for the selected organization, sourced ENTIRELY from the server
 *   (/api/family-accounts/context). The client NEVER trusts a self-chosen family
 *   id — switchFamily() POSTs the choice and re-reads the canonical context
 *   (KTD12 — active family context is a server contract, not UI state).
 * - Single-family users have their family inferred silently (no switcher noise).
 *   Multi-family users must pick before family-scoped data loads — exposed via
 *   `requiresSelection`.
 * - Exposes the viewer's ACTIVE-family enrollment permission as
 *   `canManageEnrollments` (plus the full `effectivePermissions` set) so client
 *   surfaces (catalog / enroll / private-lesson) can disable athletes the viewer
 *   cannot register BEFORE they hit the server's 403 backstop. CRITICAL: these
 *   default to false/null while inert, so consumers must gate on `enabled` + a
 *   resolved `activeFamilyAccountId`, never on `canManageEnrollments` alone.
 * - R14 STAFF CONTEXT IS SEPARATE. This provider reads only family membership; it
 *   never touches UserRoleContext / staff roles. Switching family does NOT change
 *   staff permissions. It also only fetches when the active dashboard persona is a
 *   parent/account-holder — a coach viewing the staff dashboard stays inert.
 *
 * FLAG SAFETY: when ENABLE_FAMILY_GUARDIAN_ACCESS is OFF, the provider is fully
 * inert — it never fetches, exposes `enabled: false`, an empty `families` list, a
 * null `activeFamilyAccountId`, and a no-op `switchFamily`. The API also 404s while
 * off. So a host page that reads this context behaves byte-for-byte as before.
 *
 * RECOVERY: if the server reports a stale persisted selection (revoked / deleted /
 * other-org), the provider clears its local choice and re-prompts (multi-family) or
 * silently infers (single-family) — the data never leaks from a stale family.
 *
 * ORG SWITCH (FGA-CORR-03): when currentOrganization.id changes, the provider
 * resets families / activeFamilyAccountId / requiresSelection to inert defaults
 * and enters isLoading BEFORE the new fetch runs, so a render between orgs can
 * never resolve activeFamily against the previous org's household.
 *
 * DRY: reuses useAuth + useOrganization (the canonical session/org sources) and the
 * shared family types. It does not duplicate any membership SQL — that lives in
 * FamilyAccountService behind the context route.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { useUserRole } from './UserRoleContext';
import { supabase } from '@/utils/supabase/client';
import { FEATURE_FLAGS } from '@/utils/config/featureFlags';
import { createLogger } from '@/utils/logger/devLogger';
import type { FamilyPermissionSet } from '@/types/family';

const logger = createLogger('FamilyContext');

/** One household the user can act as, in SAFE shape (matches the context route). */
export interface FamilyContextOption {
  familyAccountId: string;
  displayName: string;
  /** Disambiguating label: family name + first athlete names. */
  label: string;
  /** The household's active athlete ids — used to scope family-scoped pages. */
  athleteIds: string[];
  athleteNames: string[];
  athleteCount: number;
  isPrimaryGuardian: boolean;
  /**
   * The household head's display name, or null when unresolved. Drives the
   * #2781 Phase 4b guardian-context label for secondary guardians.
   */
  primaryGuardianName: string | null;
  /**
   * Can the viewer register/enroll this household's athletes? True for primary
   * guardians and secondaries granted "manage enrollments". Drives the disabled
   * (not hidden) treatment on the catalog/enroll/private-lesson athlete pickers.
   */
  canManageEnrollments: boolean;
  /** The viewer's full effective permission set for THIS household. */
  effectivePermissions: FamilyPermissionSet;
}

interface FamilyContextValue {
  /** True only when the feature flag is on. While false everything is inert. */
  enabled: boolean;
  /** True while the available families / active family are being resolved. */
  isLoading: boolean;
  /**
   * POSITIVE "the family scope is settled for the selected organization" signal
   * (#3935). Pages that send `activeFamilyAccountId` to the server must wait for
   * this, never for `!isLoading`: `isLoading` starts false and only flips inside
   * this provider's effect, and React runs a page's effects BEFORE its ancestor
   * provider's on the same commit — so on the commit where the organization
   * resolves, `!isLoading` reads as "ready" one commit before the family fetch
   * has even started, and the page fires an unscoped request it then corrects.
   *
   * Derived during render from the provider's inputs, so it is false from render
   * zero while anything upstream (auth role, organization) is still loading, and
   * flips to false on the SAME render an org switch or family switch begins.
   * True while inert (flag off, staff persona, no organization selected).
   */
  hasResolved: boolean;
  /** Every active household the user can act as in the selected organization. */
  families: FamilyContextOption[];
  /** The resolved active household id, or null when a choice is still required. */
  activeFamilyAccountId: string | null;
  /** The resolved active household option, or null. */
  activeFamily: FamilyContextOption | null;
  /**
   * Athlete ids in the ACTIVE household. Empty while the flag is off, or while no
   * family is selected. Family-scoped pages intersect their athlete lists with
   * this set ONLY when `enabled` is true and a family is resolved.
   *
   * FAIL-OPEN union (#1867): also includes the server-computed
   * `unlinkedGuardianAthleteIds` — athletes the viewer is a verified guardian of
   * (parent_athlete_relationships) that are ACTIVE in this org but have no active
   * family_athletes row in ANY household here. Without the union, the family gate
   * would silently hide a parent's own child from the signup pickers whenever the
   * family link was never created. The server read-repairs the missing link;
   * athletes actively linked to a DIFFERENT household are never unioned in.
   */
  activeFamilyAthleteIds: string[];
  /** Is the user the primary (main) guardian of the ACTIVE household? */
  isPrimaryGuardian: boolean;
  /**
   * Can the viewer register/enroll the ACTIVE household's athletes? True for
   * primary guardians and secondaries granted "manage enrollments". DEFAULTS TO
   * false while the flag is off / no active family — so client gates MUST key off
   * `enabled` (and a resolved `activeFamilyAccountId`) and NEVER on this flag
   * alone, otherwise flag-off users would be wrongly gated.
   */
  canManageEnrollments: boolean;
  /**
   * The viewer's full effective permission set for the ACTIVE household, or null
   * when the flag is off / no active family. Exposed for future surfaces (billing,
   * medical, profile) so they gate the same way enrollments does.
   */
  effectivePermissions: FamilyPermissionSet | null;
  /**
   * True when the user has multiple families and has not selected one yet, so
   * family-scoped pages must prompt before loading data. False for single-family
   * (inferred) users and whenever the flag is off.
   */
  requiresSelection: boolean;
  /** Switch the active household (server-validated). Resolves when complete. */
  switchFamily: (familyAccountId: string) => Promise<void>;
  /** Re-read the context from the server (e.g. after a guardian change). */
  refresh: () => Promise<void>;
}

const INERT_VALUE: FamilyContextValue = {
  enabled: false,
  isLoading: false,
  hasResolved: true,
  families: [],
  activeFamilyAccountId: null,
  activeFamily: null,
  activeFamilyAthleteIds: [],
  isPrimaryGuardian: false,
  canManageEnrollments: false,
  effectivePermissions: null,
  requiresSelection: false,
  switchFamily: async () => {},
  refresh: async () => {},
};

const FamilyContext = createContext<FamilyContextValue>(INERT_VALUE);

interface ContextApiResponse {
  enabled: boolean;
  families: FamilyContextOption[];
  activeFamilyAccountId: string | null;
  inferred: boolean;
  requiresExplicitSelection: boolean;
  recoverableIssue?: string;
  /** #1867 fail-open union — see activeFamilyAthleteIds doc above. Optional so
   *  older cached responses (missing the field) degrade to no union. */
  unlinkedGuardianAthleteIds?: string[];
}

async function authHeaders(): Promise<Record<string, string>> {
  // Mobile WebViews may not propagate cookies in time; pass the bearer token.
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
}

export function FamilyProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const { currentUser, isLoading: roleLoading } = useUserRole();

  const [families, setFamilies] = useState<FamilyContextOption[]>([]);
  const [activeFamilyAccountId, setActiveFamilyAccountId] = useState<string | null>(null);
  const [requiresSelection, setRequiresSelection] = useState(false);
  // #1867 fail-open union — server-computed; merged into activeFamilyAthleteIds.
  const [unlinkedGuardianAthleteIds, setUnlinkedGuardianAthleteIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // #3935: the organization id the CURRENT families/activeFamilyAccountId were
  // resolved for (success, rejection, or failure — all three settle the scope).
  // Compared against the selected organization at render time, so `hasResolved`
  // is false on the same render an org switch starts, before any effect runs.
  const [resolvedOrgId, setResolvedOrgId] = useState<string | null>(null);

  // Per-invocation generation token so a slow fetch cannot clobber a newer one
  // (org/persona switches can re-fire the effect rapidly). Mirrors UserRoleContext.
  const generationRef = useRef(0);

  // FGA-CORR-03: track the org the current state belongs to so an org switch
  // clears the previous org's family selection to inert defaults BEFORE the new
  // fetch resolves. Without this, activeFamily could briefly resolve against the
  // prior org's families and a page could read another org's household.
  const stateOrgRef = useRef<string | null>(null);

  const flagOn = FEATURE_FLAGS.ENABLE_FAMILY_GUARDIAN_ACCESS;

  // Family context only applies to the parent/account-holder dashboard. A coach
  // viewing the staff dashboard must stay inert so switching family can never
  // touch staff state (R14). userType flips to 'parent' when in parent persona.
  const isParentPersona = currentUser?.userType === 'parent';

  const fetchContext = useCallback(
    async (requestedFamilyAccountId?: string) => {
      if (!flagOn || !user?.id || !currentOrganization?.id || !isParentPersona) {
        return;
      }
      const generation = ++generationRef.current;
      const isCurrent = () => generation === generationRef.current;
      const organizationId = currentOrganization.id;

      setIsLoading(true);
      try {
        const params = new URLSearchParams({ organizationId });
        if (requestedFamilyAccountId) {
          params.set('familyAccountId', requestedFamilyAccountId);
        }
        const res = await fetch(`/api/family-accounts/context?${params.toString()}`, {
          headers: await authHeaders(),
        });
        if (!res.ok) {
          // 404 while flag off (defensive) or a transient error — stay inert.
          if (!isCurrent()) return;
          setFamilies([]);
          setActiveFamilyAccountId(null);
          setRequiresSelection(false);
          setUnlinkedGuardianAthleteIds([]);
          setResolvedOrgId(organizationId);
          return;
        }
        const data = (await res.json()) as ContextApiResponse;
        if (!isCurrent()) return;
        setFamilies(data.families ?? []);
        setActiveFamilyAccountId(data.activeFamilyAccountId);
        setRequiresSelection(!!data.requiresExplicitSelection);
        setUnlinkedGuardianAthleteIds(data.unlinkedGuardianAthleteIds ?? []);
        setResolvedOrgId(organizationId);
        if (data.recoverableIssue) {
          // The server already ignored the stale selection; nothing leaks. We
          // simply log so support can see a stale preference was cleared.
          logger.info('Family context recoverable issue', {
            issue: data.recoverableIssue,
            organizationId: currentOrganization.id,
          });
        }
      } catch (err) {
        logger.error('Failed to load family context', {
          organizationId: currentOrganization?.id,
          err,
        });
        if (!isCurrent()) return;
        setFamilies([]);
        setActiveFamilyAccountId(null);
        setRequiresSelection(false);
        setUnlinkedGuardianAthleteIds([]);
        setResolvedOrgId(organizationId);
      } finally {
        if (isCurrent()) setIsLoading(false);
      }
    },
    [flagOn, user?.id, currentOrganization?.id, isParentPersona]
  );

  // Resolve (or re-resolve) whenever the user, org, persona, or flag changes.
  // When the flag is off / not a parent, reset to inert and DO NOT fetch.
  useEffect(() => {
    if (!flagOn || !isParentPersona) {
      stateOrgRef.current = null;
      setFamilies([]);
      setActiveFamilyAccountId(null);
      setRequiresSelection(false);
      setUnlinkedGuardianAthleteIds([]);
      setResolvedOrgId(null);
      setIsLoading(false);
      return;
    }

    const orgId = currentOrganization?.id ?? null;
    // FGA-CORR-03: when the organization changes, drop the prior org's family
    // selection to inert defaults and enter loading BEFORE fetchContext() runs,
    // so no render reads a stale family from a different org.
    if (orgId !== stateOrgRef.current) {
      stateOrgRef.current = orgId;
      setFamilies([]);
      setActiveFamilyAccountId(null);
      setRequiresSelection(false);
      setUnlinkedGuardianAthleteIds([]);
      setResolvedOrgId(null);
      setIsLoading(true);
    }

    fetchContext();
  }, [flagOn, isParentPersona, currentOrganization?.id, fetchContext]);

  // #3935: settled-scope signal, derived at render (see the interface note).
  // Inert viewers (staff persona, no organization) have nothing to resolve.
  // A parent with an organization is settled only once THIS organization's
  // context has come back and no switch/refresh is in flight.
  const selectedOrgId = currentOrganization?.id ?? null;
  const hasResolved = (() => {
    if (roleLoading || orgLoading) return false;
    if (!isParentPersona) return true;
    if (!selectedOrgId) return true;
    return resolvedOrgId === selectedOrgId && !isLoading;
  })();

  const switchFamily = useCallback(
    async (familyAccountId: string) => {
      if (!flagOn || !user?.id || !currentOrganization?.id || !isParentPersona) {
        return;
      }
      const generation = ++generationRef.current;
      const isCurrent = () => generation === generationRef.current;
      const organizationId = currentOrganization.id;
      setIsLoading(true);
      try {
        const res = await fetch('/api/family-accounts/context', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({
            organizationId,
            familyAccountId,
          }),
        });
        const data = (await res.json()) as ContextApiResponse;
        if (!isCurrent()) return;
        // Either branch is a canonical read of this organization's context, so
        // it settles the scope too — a switch that supersedes an in-flight
        // fetchContext() (same generation counter) must not leave
        // `resolvedOrgId` unstamped, or `hasResolved` would stay false.
        if (res.ok) {
          setFamilies(data.families ?? []);
          setActiveFamilyAccountId(data.activeFamilyAccountId);
          setRequiresSelection(!!data.requiresExplicitSelection);
          setUnlinkedGuardianAthleteIds(data.unlinkedGuardianAthleteIds ?? []);
          setResolvedOrgId(organizationId);
        } else {
          // Rejected (e.g. not a guardian of that family). Re-sync to the
          // server's canonical view so we never show a family the user lost.
          setFamilies(data.families ?? []);
          setActiveFamilyAccountId(data.activeFamilyAccountId);
          setRequiresSelection(!!data.requiresExplicitSelection);
          setUnlinkedGuardianAthleteIds(data.unlinkedGuardianAthleteIds ?? []);
          setResolvedOrgId(organizationId);
          logger.warn('Family switch rejected', {
            organizationId,
            issue: data.recoverableIssue,
          });
        }
      } catch (err) {
        logger.error('Failed to switch family', {
          organizationId: currentOrganization?.id,
          err,
        });
      } finally {
        if (isCurrent()) setIsLoading(false);
      }
    },
    [flagOn, user?.id, currentOrganization?.id, isParentPersona]
  );

  const refresh = useCallback(async () => {
    await fetchContext();
  }, [fetchContext]);

  const activeFamily = useMemo(
    () => families.find((f) => f.familyAccountId === activeFamilyAccountId) ?? null,
    [families, activeFamilyAccountId]
  );

  const value = useMemo<FamilyContextValue>(() => {
    if (!flagOn) {
      return INERT_VALUE;
    }
    return {
      enabled: true,
      isLoading,
      hasResolved,
      families,
      activeFamilyAccountId,
      activeFamily,
      // #1867 FAIL-OPEN: union the active household's athletes with the caller's
      // verified-guardian athletes that have no family link anywhere in this org,
      // so the family gate can never hide a parent's own org-active child. Empty
      // while no family is resolved — the gate is inert then, so no union needed.
      activeFamilyAthleteIds: activeFamily
        ? Array.from(new Set([...activeFamily.athleteIds, ...unlinkedGuardianAthleteIds]))
        : [],
      isPrimaryGuardian: activeFamily?.isPrimaryGuardian ?? false,
      // Tracks the ACTIVE family — auto-recomputes on switch via `activeFamily`.
      // Defaults to false when no family is resolved; gates MUST also require
      // `enabled` + a resolved activeFamilyAccountId (never this flag alone).
      canManageEnrollments: activeFamily?.canManageEnrollments ?? false,
      effectivePermissions: activeFamily?.effectivePermissions ?? null,
      requiresSelection,
      switchFamily,
      refresh,
    };
  }, [
    flagOn,
    isLoading,
    hasResolved,
    families,
    activeFamilyAccountId,
    activeFamily,
    unlinkedGuardianAthleteIds,
    requiresSelection,
    switchFamily,
    refresh,
  ]);

  return <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>;
}

export { FamilyContext };
